/**
 * stripe - memoized Stripe.js loader (P5-D17-F9, T-F9-3).
 *
 * `loadStripe` must run at most once per (publishableKey, connectedAccount)
 * pair: Stripe.js injects a singleton `<script>` and re-`loadStripe`-ing the
 * same account thrashes it. The connected account is REQUIRED — F9 always
 * mounts against the tenant's Stripe Connect account (A1), so the loader passes
 * `{ stripeAccount }`.
 *
 * Memoization lives at module scope so every CardCheckoutPanel mount/remount
 * for the same account reuses one promise (Cross-Task Decision: intent-once /
 * mount-stability is built on a stable Stripe instance).
 */

import { loadStripe, type Stripe } from '@stripe/stripe-js';

const cache = new Map<string, Promise<Stripe | null>>();

/**
 * Load (or reuse) the Stripe.js instance for a publishable key + connected
 * account. The SAME pair returns the SAME promise; a different account triggers
 * a fresh `loadStripe` call scoped to that account.
 */
export function getStripe(publishableKey: string, stripeAccountId: string): Promise<Stripe | null> {
  const key = `${publishableKey}::${stripeAccountId}`;
  let promise = cache.get(key);
  if (!promise) {
    promise = loadStripe(publishableKey, { stripeAccount: stripeAccountId });
    cache.set(key, promise);
  }
  return promise;
}
